import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface WebDrawerControl {
  /** Phone widths only: the sidebar is off-canvas, so pages paint a menu button. */
  isMobile: boolean;
  toggleDrawer: () => void;
}

const WebDrawerContext = createContext<WebDrawerControl | null>(null);

/**
 * Hands the sidebar toggle down to page headers so they can render the drawer
 * hamburger *in flow*, on the same row as their title and action button,
 * instead of each screen reserving space for the shell's floating fallback.
 *
 * Provided by ProtectedWebShell (it owns both values).
 */
export function WebDrawerProvider({
  isMobile,
  toggleDrawer,
  children,
}: WebDrawerControl & { children: ReactNode }) {
  const value = useMemo<WebDrawerControl>(
    () => ({ isMobile, toggleDrawer }),
    [isMobile, toggleDrawer],
  );
  return (
    <WebDrawerContext.Provider value={value}>
      {children}
    </WebDrawerContext.Provider>
  );
}

/** Null outside the protected shell — public routes have no drawer. */
export function useWebDrawer(): WebDrawerControl | null {
  return useContext(WebDrawerContext);
}
